import React from "react";
import { Box, Grid, Typography } from "@mui/material";
import { EmojiObjects, LocalShipping, Lock } from "@mui/icons-material";
import { motion } from "framer-motion";

const WonIcon = (props: any) => (
  <svg width={24} height={24} viewBox="0 0 24 24" fill="none" {...props}>
    <path d="M4 6L8 18L12 10L16 18L20 6" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" fill="none" />
    <path d="M2 9H22" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    <path d="M2 13H22" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
  </svg>
);

const steps = [
  {
    icon: <EmojiObjects sx={{ fontSize: 24, color: '#fff' }} />,
    title: "간편 신청",
    description: "회원가입 없이 빠르게 신청",
    gradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    glowColor: 'rgba(102, 126, 234, 0.35)',
    accentColor: '#667eea',
  },
  {
    icon: <LocalShipping sx={{ fontSize: 24, color: '#fff' }} />,
    title: "택배 기사 방문",
    description: "직접 방문하여 빠르게 수거",
    gradient: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
    glowColor: 'rgba(240, 147, 251, 0.35)',
    accentColor: '#f093fb',
  },
  {
    icon: <Lock sx={{ fontSize: 24, color: '#fff' }} />,
    title: "검수 결과 안내",
    description: "투명한 검수 후 합리적 가격 안내",
    gradient: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
    glowColor: 'rgba(79, 172, 254, 0.35)',
    accentColor: '#4facfe',
  },
  {
    icon: <WonIcon style={{ color: '#fff' }} />,
    title: "빠른 입금처리",
    description: "최종 확정 후 1~2일 내 입금",
    gradient: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
    glowColor: 'rgba(67, 233, 123, 0.35)',
    accentColor: '#43e97b',
  },
];

const StepSection: React.FC = () => (
  <Box id="steps-section" sx={{
    py: { xs: 4, md: 8 },
    background: 'linear-gradient(180deg, #f8fafc 0%, #f1f5f9 100%)',
    position: 'relative',
    overflow: 'hidden',
  }}>
    {/* 배경 장식 */}
    <Box sx={{
      position: 'absolute',
      top: '-30%',
      right: '-10%',
      width: 400,
      height: 400,
      background: 'radial-gradient(circle, rgba(59,130,246,0.04), transparent 70%)',
      borderRadius: '50%',
      pointerEvents: 'none',
    }} />

    <Box sx={{ maxWidth: { xs: '100%', md: '50%' }, mx: 'auto', px: { xs: 2, md: 0 }, position: 'relative', zIndex: 1 }}>
      {/* 헤더 */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        viewport={{ once: true }}
      >
        <Box sx={{ textAlign: 'center', mb: { xs: 2.5, md: 5 } }}>
          <Typography
            fontWeight={800}
            sx={{
              fontSize: { xs: '1.2rem', md: '1.6rem' },
              color: '#0f172a',
              mb: 1,
              letterSpacing: '-0.02em',
            }}
          >
            간단한 판매 절차
          </Typography>
          <Typography sx={{
            fontSize: { xs: '0.85rem', md: '0.95rem' },
            color: '#64748b',
            maxWidth: 400,
            mx: 'auto',
          }}>
            4단계로 쉽고 빠르게 중고 기기를 판매하세요
          </Typography>
        </Box>
      </motion.div>

      {/* 4단계 카드 */}
      <Grid container spacing={{ xs: 2, md: 3 }}>
        {steps.map((step, index) => (
          <Grid item xs={6} md={3} key={index}>
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              viewport={{ once: true }}
            >
              <Box
                sx={{
                  background: '#fff',
                  borderRadius: 3,
                  p: { xs: 2.5, md: 3 },
                  height: '100%',
                  textAlign: 'center',
                  position: 'relative',
                  overflow: 'hidden',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.04), 0 4px 12px rgba(0,0,0,0.03)',
                  transition: 'all 0.35s cubic-bezier(0.4, 0, 0.2, 1)',
                  '&:hover': {
                    transform: 'translateY(-6px)',
                    boxShadow: `0 12px 32px ${step.glowColor}, 0 4px 12px rgba(0,0,0,0.06)`,
                  },
                  // 상단 그라디언트 라인
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    height: 3,
                    background: step.gradient,
                    borderRadius: '12px 12px 0 0',
                  },
                }}
              >
                {/* 스텝 번호 */}
                <Typography sx={{
                  fontSize: '0.6rem',
                  fontWeight: 800,
                  color: step.accentColor,
                  letterSpacing: '0.1em',
                  mb: 2,
                  opacity: 0.7,
                }}>
                  STEP 0{index + 1}
                </Typography>

                {/* 아이콘 */}
                <Box sx={{
                  width: 52,
                  height: 52,
                  borderRadius: '16px',
                  background: step.gradient,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mx: 'auto',
                  mb: 2,
                  boxShadow: `0 6px 20px ${step.glowColor}`,
                  transition: 'transform 0.3s ease',
                  '&:hover': {
                    transform: 'scale(1.08) rotate(-3deg)',
                  },
                }}>
                  {step.icon}
                </Box>

                {/* 제목 */}
                <Typography fontWeight={700} sx={{
                  fontSize: { xs: '0.9rem', md: '1rem' },
                  color: '#0f172a',
                  mb: 0.8,
                  letterSpacing: '-0.01em',
                }}>
                  {step.title}
                </Typography>

                {/* 설명 */}
                <Typography sx={{
                  fontSize: { xs: '0.73rem', md: '0.8rem' },
                  color: '#94a3b8',
                  lineHeight: 1.6,
                }}>
                  {step.description}
                </Typography>
              </Box>
            </motion.div>
          </Grid>
        ))}
      </Grid>

      {/* 하단 연결선 (데스크탑) */}
      <Box sx={{
        display: { xs: 'none', md: 'flex' },
        justifyContent: 'center',
        alignItems: 'center',
        mt: 3,
        gap: 1,
      }}>
        {[0, 1, 2].map((i) => (
          <React.Fragment key={i}>
            <Box sx={{
              width: 6,
              height: 6,
              borderRadius: '50%',
              background: steps[i].gradient,
              boxShadow: `0 0 8px ${steps[i].glowColor}`,
            }} />
            <Box sx={{
              flex: 1,
              maxWidth: 120,
              height: 2,
              background: `linear-gradient(90deg, ${steps[i].accentColor}40, ${steps[i + 1].accentColor}40)`,
              borderRadius: 1,
            }} />
          </React.Fragment>
        ))}
        <Box sx={{
          width: 6,
          height: 6,
          borderRadius: '50%',
          background: steps[3].gradient,
          boxShadow: `0 0 8px ${steps[3].glowColor}`,
        }} />
      </Box>
    </Box>
  </Box>
);

export default StepSection;
